import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";

import {
  chargebackStaffQuery,
  chargebackTransactionsQuery,
  confirmChargebackTransactionMutation,
} from "./queries";

const TOAST_MS = 3400;

export const PAGE_SIZE = 50;

export function dayRange(from, to, timeZone) {
  const bound = (day, edge) => {
    if (!day) return null;

    const date = DateTime.fromISO(day, timeZone ? { zone: timeZone } : undefined);

    return date.isValid ? date[edge]("day").toUTC().toISO() : null;
  };

  return { from: bound(from, "startOf"), to: bound(to, "endOf") };
}

export default function useChargebackTransactions({
  installationId,
  status,
  from,
  to,
  confirmedById,
  timeZone,
}) {
  const [toast, setToast] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const [page, setPage] = useState(0);
  const timer = useRef(null);

  useEffect(() => setPage(0), [status, from, to, confirmedById]);

  const range = dayRange(from, to, timeZone);

  const variables = {
    installationId,
    status,
    from: range.from,
    to: range.to,
    confirmedById: confirmedById || null,
    offset: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  };

  const { data, loading, error, refetch } = useQuery(
    chargebackTransactionsQuery,
    {
      variables,
      skip: !installationId,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data: staffData } = useQuery(chargebackStaffQuery, {
    variables: { installationId },
    skip: !installationId,
  });

  const [confirmChargeback, confirmState] = useMutation(
    confirmChargebackTransactionMutation,
  );

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (message) => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), TOAST_MS);
  };

  const response = data?.chargebackTransactions;
  const rows = response?.items || [];

  const count = response?.count ?? 0;
  const totalAud = response?.totalAud ?? "0";
  const currency = response?.currency ?? null;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const stepBackIfEmptied = () => {
    if (page > 0 && rows.length === 1) setPage((current) => current - 1);
  };

  const confirm = async (id, message) => {
    setMutationError(null);

    try {
      await confirmChargeback({ variables: { id } });
      stepBackIfEmptied();
      flash(message);
    } catch (err) {
      setMutationError(err);
      return false;
    }

    return true;
  };

  return {
    rows,
    staff: staffData?.chargebackTransactionStaff || [],
    count,
    totalAud,
    currency,
    loading,
    error,
    mutationError,
    confirming: confirmState.loading,
    toast,
    confirm,
    page,
    pages,
    from: page * PAGE_SIZE + 1,
    through: page * PAGE_SIZE + rows.length,
    hasPrevious: page > 0,
    hasNext: (page + 1) * PAGE_SIZE < count,
    previousPage: () => setPage((current) => Math.max(0, current - 1)),
    nextPage: () => setPage((current) => current + 1),
    refetch: (vars = {}) => refetch({ ...variables, ...vars }),
  };
}
